import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "./components/Layout";
import Dashboard from "./components/Dashboard";
import SetupDetail from "./components/SetupDetail";
import Settings from "./components/Settings";
import Alerts from "./components/Alerts";
import AddSetupSheet from "./components/AddSetupSheet";
import StatusUpdateSheet from "./components/StatusUpdateSheet";
import api from "./lib/api";
import { applyTheme, getStoredTheme, toggleTheme } from "./lib/theme";
import { isAttractive } from "./lib/proximity";
import { toast } from "sonner";

function App() {
  const [setups, setSetups] = useState([]);
  const [, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editSetup, setEditSetup] = useState(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  // Watchlist state (lifted from Dashboard)
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState("ALLE");
  const [onlyAttractive, setOnlyAttractive] = useState(false);
  const [polling, setPolling] = useState(false);

  useEffect(() => { applyTheme(theme); }, [theme]);

  const handleToggleTheme = () => {
    toggleTheme();
    setTheme(getStoredTheme());
  };

  const refresh = useCallback(async () => {
    try {
      const data = await api.listSetups();
      setSetups(data);
    } catch (e) {
      console.error("Failed to load setups", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  // Auto-select first setup
  useEffect(() => {
    if (!selectedId && setups.length > 0) setSelectedId(setups[0].id);
    if (selectedId && !setups.find(s => s.id === selectedId)) {
      setSelectedId(setups.length > 0 ? setups[0].id : null);
    }
  }, [setups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePoll = async () => {
    setPolling(true);
    try {
      const r = await api.pollNow();
      toast.success(`${r.setups_polled} Setups geprüft. ${r.new_alerts} neue Alerts.`);
      refresh();
    } catch {
      toast.error("Polling fehlgeschlagen.");
    } finally {
      setPolling(false);
    }
  };

  const filteredSetups = setups.filter(s => {
    if (category !== "ALLE" && s.category !== category) return false;
    if (filter && !s.asset_name.toLowerCase().includes(filter.toLowerCase()) &&
        !s.symbol.toLowerCase().includes(filter.toLowerCase())) return false;
    if (onlyAttractive && !isAttractive(s)) return false;
    return true;
  });

  const attractiveCount = setups.filter(isAttractive).length;
  const categories = ["ALLE", ...Array.from(new Set(setups.map(s => s.category)))];

  const openAdd = () => { setEditSetup(null); setSheetOpen(true); };
  const openEdit = (s) => { setEditSetup(s); setSheetOpen(true); };

  return (
    <div className="App">
      <Toaster theme={theme} position="top-right" richColors />
      <BrowserRouter>
        <Layout
          onAddSetup={openAdd}
          onStatusUpdate={() => setStatusOpen(true)}
          sidebarOpen={sidebarOpen}
          onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
          theme={theme}
          onToggleTheme={handleToggleTheme}
          setups={filteredSetups}
          allSetups={setups}
          selectedId={selectedId}
          onSelect={(id) => { setSelectedId(id); setSidebarOpen(false); }}
          filter={filter}
          onFilterChange={setFilter}
          category={category}
          onCategoryChange={setCategory}
          categories={categories}
          onlyAttractive={onlyAttractive}
          onToggleAttractive={() => setOnlyAttractive(v => !v)}
          attractiveCount={attractiveCount}
          polling={polling}
          onPoll={handlePoll}
        >
          <Routes>
            <Route path="/" element={
              <Dashboard
                setups={filteredSetups}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onAddSetup={openAdd}
                onEditSetup={openEdit}
                onRefresh={refresh}
              />
            } />
            <Route path="/setups/:id" element={<SetupDetail />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/alerts" element={<Alerts />} />
          </Routes>
        </Layout>
        <AddSetupSheet
          open={sheetOpen}
          onOpenChange={(v) => { setSheetOpen(v); if (!v) setEditSetup(null); }}
          editSetup={editSetup}
          onCreated={() => refresh()}
          onUpdated={() => refresh()}
        />
        <StatusUpdateSheet
          open={statusOpen}
          onOpenChange={setStatusOpen}
          onDone={() => refresh()}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
